import { UserRole } from '../types';

export interface Permission {
  resource: string;
  actions: string[];
}

export interface RolePermissions {
  [role: string]: Permission[];
}

export const DEFAULT_PERMISSIONS: RolePermissions = {
  [UserRole.SUPERADMIN]: [
    { resource: 'all', actions: ['*'] }
  ],
  [UserRole.MANAGER]: [
    { resource: 'dashboard', actions: ['view', 'admin'] },
    { resource: 'projects', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'tasks', actions: ['view', 'create', 'edit', 'delete', 'assign'] },
    { resource: 'timeTracking', actions: ['view', 'create', 'edit', 'delete', 'view-all'] },
    { resource: 'teams', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'crm', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'sales', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'sourcing', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'research', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'requests', actions: ['view', 'create', 'edit', 'delete', 'approve'] },
    { resource: 'users', actions: ['view', 'edit'] },
  ],
  [UserRole.MEDIA_BUYER]: [
    { resource: 'dashboard', actions: ['view'] },
    { resource: 'tasks', actions: ['view', 'edit'] },
    { resource: 'sales', actions: ['view', 'create', 'edit', 'delete'] },
    { resource: 'timeTracking', actions: ['view', 'create', 'edit'] },
    { resource: 'requests', actions: ['view', 'create'] },
    { resource: 'paymentRequests', actions: ['view', 'create'] },
  ],
  [UserRole.MEMBER]: [
    { resource: 'dashboard', actions: ['view'] },
    { resource: 'tasks', actions: ['view', 'edit'] },
    { resource: 'timeTracking', actions: ['view', 'create', 'edit'] },
    { resource: 'requests', actions: ['view', 'create'] },
    { resource: 'paymentRequests', actions: ['view', 'create'] },
  ],
  [UserRole.GUEST]: [
    { resource: 'dashboard', actions: ['view'] },
    { resource: 'timeTracking', actions: ['view'] },
  ],
};

export function hasPermission(
  userRole: UserRole,
  resource: string,
  action: string,
  customPermissions?: any
): boolean {
  // Debug logging
  console.log('hasPermission check:', {
    userRole,
    resource,
    action,
    customPermissions: customPermissions?.[resource],
    hasCustomPermission: !!(customPermissions && customPermissions[resource])
  });

  // Superadmin always has access
  if (userRole === UserRole.SUPERADMIN) {
    return true;
  }

  // Dashboard is always accessible for all logged in users
  if (resource === 'dashboard' && action === 'view') {
    return true;
  }

  // Check custom permissions first if provided
  if (customPermissions && customPermissions[resource]) {
    const perm = customPermissions[resource];
    // Check for sideMenu permission (for navigation)
    if (action === 'view' && perm.sideMenu) {
      console.log('✅ Custom permission granted via sideMenu');
      return true;
    }
    // Check for mainCard permission (for dashboard cards)
    if (action === 'mainCard' && perm.mainCard) {
      return true;
    }
    // For sales, also check specific actions
    if (resource === 'sales') {
      if (action === 'create' && perm.sideMenu) {
        return true; // Allow creating stats if they have sales access
      }
      if (action === 'edit' && perm.sideMenu) {
        return true; // Allow editing stats if they have sales access
      }
    }
  }

  // Members only have access to dashboard and their custom permissions
  if (userRole === UserRole.MEMBER) {
    // Always allow dashboard view
    if (resource === 'dashboard' && action === 'view') {
      return true;
    }
    // For other resources, check if they have custom permissions
    // If custom permissions were already checked above and denied, return false
    if (!customPermissions || !customPermissions[resource]) {
      console.log('❌ Member denied access - no custom permissions for resource');
      return false;
    }
    // If we get here, they have custom permissions but they were insufficient
    return false;
  }

  // Check default role permissions
  const rolePermissions = DEFAULT_PERMISSIONS[userRole] || [];
  const permission = rolePermissions.find(p => p.resource === resource || p.resource === 'all');
  
  if (!permission) {
    return false;
  }

  // For mainCard action, check if user has view permission
  if (action === 'mainCard') {
    return permission.actions.includes('view') || permission.actions.includes('*');
  }

  return permission.actions.includes(action) || permission.actions.includes('*');
}

export function getAccessibleResources(userRole: UserRole, customPermissions?: Permission[]): string[] {
  const resources = new Set<string>();
  
  // Add default role resources
  const rolePermissions = DEFAULT_PERMISSIONS[userRole] || [];
  rolePermissions.forEach(p => {
    if (p.resource !== 'all') {
      resources.add(p.resource);
    }
  });

  // Add custom permission resources
  if (customPermissions) {
    customPermissions.forEach(p => resources.add(p.resource));
  }

  // Superadmin gets all resources
  if (userRole === UserRole.SUPERADMIN) {
    return ['dashboard', 'projects', 'tasks', 'timeTracking', 'teams', 'crm', 'sales', 'sourcing', 'research', 'requests', 'users', 'reports'];
  }
  
  // Media Buyer gets sales
  if (userRole === UserRole.MEDIA_BUYER) {
    resources.add('sales');
  }

  return Array.from(resources);
}